import { Link } from "react-router";
import { useRef, useState, useEffect, useCallback } from "react";
import { useUser } from "../../lib/contexts/UserContextUtils";
import { useMcpStatus } from "../../lib/contexts/McpStatusContextUtils";
import { userLogout } from "../../lib/api/Api";
import { FaDiscord } from "react-icons/fa";
import {
  ArrowLeftOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CpuChipIcon,
  ServerStackIcon,
} from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next"; // Added useTranslation import

export function UserDropdown({
  direction = "down",
  noResponsive = false,
}: {
  direction?: "down" | "up";
  noResponsive?: boolean;
}) {
  const { t } = useTranslation(); // Added useTranslation hook
  const { user, setUser } = useUser();
  // A change an assistant queued expires on its own. The only way a user who
  // is not already on /connections learns one is waiting is this badge.
  const { pending, urgent } = useMcpStatus();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = useCallback(() => {
    userLogout();
    setUser(null);
  }, [setUser]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  return (
    <div className="relative w-full sm:w-auto" ref={dropdownRef}>
      <button
        onClick={() => setDropdownOpen((prev) => !prev)}
        aria-label={
          pending > 0
            ? t("UserDropdown.pendingApprovalsAria", { count: pending })
            : undefined
        }
        className="cursor-pointer relative w-full text-left flex items-center gap-2.5 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 sm:pr-4 transition-all duration-300"
      >
        {/* Visible with the menu closed, which is the state it has to work in. */}
        {pending > 0 && (
          <span
            aria-hidden="true"
            className={`absolute -top-1 -right-1 flex items-center justify-center min-w-4.5 h-4.5 px-1 rounded-full text-[10px] font-bold text-white ring-2 ring-gray-900 ${
              urgent ? "bg-amber-500 motion-safe:animate-pulse" : "bg-indigo-500"
            }`}
          >
            {pending > 9 ? "9+" : pending}
          </span>
        )}
        {user.avatar ? (
          <img
            alt={t("UserDropdown.discordUserAvatar")} // Translated
            src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=64`}
            className="w-7 h-7 rounded-full object-cover flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-discord text-white">
            <FaDiscord className="text-lg"></FaDiscord>
          </div>
        )}
        <span
          className={`notranslate ${
            noResponsive ? "inline" : "hidden sm:inline"
          } font-medium truncate max-w-[120px]`}
        >
          {user.username}
        </span>
        {direction === "up" ? (
          <ChevronUpIcon className="w-4 h-4 ml-auto text-current" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 ml-auto text-current [@media(max-width:350px)]:hidden" />
        )}
      </button>

      {dropdownOpen && (
        <div
          className={`absolute ${
            direction === "up" ? "bottom-full mb-2" : "top-full mt-2"
          } ${
            noResponsive ? "left-0 right-0" : "right-0 w-55"
          } bg-gray-700 text-white rounded-lg shadow-lg z-50 overflow-hidden`}
        >
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-2 hover:bg-gray-600 transition"
            onClick={() => setDropdownOpen(false)}
          >
            <ServerStackIcon className="w-4 h-4 text-indigo-400" />
            {t("UserDropdown.serversLink")}
          </Link>

          <Link
            to="/upgrade"
            className="flex items-center gap-3 px-4 py-2 hover:bg-gray-600 transition"
            onClick={() => setDropdownOpen(false)}
          >
            <ArrowUpCircleIcon className="w-4 h-4 text-indigo-400" />
            {t("UserDropdown.upgradeLink")}
          </Link>

          <Link
            to="/connections"
            className="flex items-center gap-3 px-4 py-2 hover:bg-gray-600 transition"
            onClick={() => setDropdownOpen(false)}
          >
            <CpuChipIcon className="w-4 h-4 text-indigo-400" />
            {t("UserDropdown.connectionsLink")}
            {pending > 0 && (
              <span
                className={`ml-auto min-w-5 px-1.5 py-0.5 rounded-full text-[11px] font-semibold text-center ${
                  urgent
                    ? "bg-amber-500/20 text-amber-300"
                    : "bg-indigo-500/25 text-indigo-200"
                }`}
              >
                {pending}
              </span>
            )}
          </Link>

          <a
            href={import.meta.env.VITE_DISCORD_INVITE}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2 hover:bg-gray-600 transition"
            onClick={() => setDropdownOpen(false)}
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4 text-indigo-400" />
            {t("UserDropdown.supportLink")}
          </a>

          <div className="my-1 h-px bg-gray-600" />

          <button
            onClick={handleLogout}
            className="cursor-pointer w-full flex items-center gap-3 text-left px-4 py-2 hover:bg-gray-600 transition"
          >
            <ArrowLeftOnRectangleIcon className="w-4 h-4 text-red-400" />
            {t("UserDropdown.logoutButton")}
          </button>
        </div>
      )}
    </div>
  );
}
